<template>
  <div class="admin-contract-upload-component text-start" v-if="accountData.id">
    <!-- Checkbox for if this file will be considered signed or not -->
    <div class="form-check mb-3">
      <label class="form-check-label fw-bold" for="isSigned">
        Envelope requires user signature
      </label>
      <input
        class="form-check-input"
        type="checkbox"
        id="isSigned"
        v-model="requiresSignature"
      />
    </div>

    <!-- Multi-signer selection -->
    <div v-if="requiresSignature" class="mb-4">
      <label class="form-label fw-bold">Select signers</label>
      <div class="card p-3">
        <div v-if="loadingAccounts" class="text-center py-3">
          <span class="spinner-border spinner-border-sm me-2"></span>
          Loading company accounts...
        </div>
        <div v-else-if="companyAccounts.length === 0" class="text-muted py-2">
          No accounts found for this company.
        </div>
        <div v-else>
          <div
            v-for="account in companyAccounts"
            :key="account.id"
            class="form-check"
          >
            <input
              class="form-check-input"
              type="checkbox"
              :id="`signer-${account.id}`"
              :value="account.id"
              v-model="selectedSigners"
            />
            <label class="form-check-label" :for="`signer-${account.id}`">
              {{ account.name }} ({{ account.email }})
            </label>
          </div>
        </div>
        <small class="text-muted mt-2">
          Select one or more signers for this document envelope.
        </small>
        <div v-if="signersError" class="text-danger mt-1">
          {{ signersError }}
        </div>
      </div>
    </div>

    <!-- File Upload -->
    <div class="mb-4">
      <label class="form-label fw-bold">Upload files *</label>
      <div class="border rounded p-3">
        <input
          type="file"
          class="form-control mb-3"
          id="fileUpload"
          multiple
          ref="fileInput"
          @change="handleFileSelection"
        />
        <small class="text-muted d-block mb-2">
          Select one or more files to include in this envelope. Reminder: .adoc
          files are supported only when uploading signed contracts.
        </small>
      </div>
    </div>

    <!-- Individual File Cards -->
    <div v-if="fileData.length > 0" class="mb-4">
      <h6 class="mb-3">Configure Files ({{ fileData.length }}):</h6>

      <div v-for="(file, index) in fileData" :key="index" class="card mb-3">
        <div
          class="card-header d-flex justify-content-between align-items-center"
        >
          <div>
            <strong>{{ file.file.name }}</strong>
            <small class="text-muted ms-2">{{
              formatFileSize(file.file.size)
            }}</small>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            @click="removeFile(index)"
          >
            <font-awesome-icon icon="times" />
          </button>
        </div>

        <div class="card-body">
          <!-- Contract Key Selection -->
          <div class="mb-3">
            <label :for="`contractKey-${index}`" class="form-label fw-bold"
              >Contract key *</label
            >
            <select
              class="form-control"
              :id="`contractKey-${index}`"
              v-model="file.contract_key_selection"
              @change="onContractKeyChange(index)"
              @blur="
                v$?.fileData?.$each?.[index]?.contract_key_selection?.$touch()
              "
              :class="{
                'is-invalid':
                  v$?.fileData?.$each?.[index]?.contract_key_selection
                    ?.$dirty &&
                  v$?.fileData?.$each?.[index]?.contract_key_selection?.$error,
              }"
            >
              <option value="">Select a contract key</option>
              <optgroup label="Authorization">
                <option
                  v-for="key in contractKeysByCategory.authorization"
                  :key="key.key"
                  :value="key.key"
                >
                  {{ key.title }} ({{ key.key }})
                </option>
              </optgroup>
              <optgroup label="Membership">
                <option
                  v-for="key in contractKeysByCategory.membership"
                  :key="key.key"
                  :value="key.key"
                >
                  {{ key.title }} ({{ key.key }})
                </option>
              </optgroup>
              <optgroup label="Device Registration">
                <option
                  v-for="key in contractKeysByCategory.device_registration"
                  :key="key.key"
                  :value="key.key"
                >
                  {{ key.title }} ({{ key.key }})
                </option>
              </optgroup>
              <optgroup label="Terms & Service">
                <option
                  v-for="key in contractKeysByCategory.terms_service"
                  :key="key.key"
                  :value="key.key"
                >
                  {{ key.title }} ({{ key.key }})
                </option>
              </optgroup>
              <optgroup label="Upload Files">
                <option
                  v-for="key in contractKeysByCategory.upload_file"
                  :key="key.key"
                  :value="key.key"
                >
                  {{ key.title }} ({{ key.key }})
                </option>
              </optgroup>
              <optgroup label="Grid Contract">
                <option
                  v-for="key in contractKeysByCategory.grid_contract"
                  :key="key.key"
                  :value="key.key"
                >
                  {{ key.title }} ({{ key.key }})
                </option>
              </optgroup>
              <optgroup label="Other">
                <option
                  v-for="key in contractKeysByCategory.other"
                  :key="key.key"
                  :value="key.key"
                >
                  {{ key.title }} ({{ key.key }})
                </option>
              </optgroup>
              <option value="custom">Other (custom)</option>
            </select>

            <!-- Custom contract key input -->
            <div v-if="file.contract_key_selection === 'custom'" class="mt-2">
              <input
                type="text"
                class="form-control"
                :id="`customContractKey-${index}`"
                v-model="file.custom_contract_key"
                placeholder="contract_"
                @blur="
                  v$?.fileData?.$each?.[index]?.custom_contract_key?.$touch()
                "
                :class="{
                  'is-invalid':
                    v$?.fileData?.$each?.[index]?.custom_contract_key?.$dirty &&
                    v$?.fileData?.$each?.[index]?.custom_contract_key?.$error,
                }"
              />
              <div
                v-if="
                  v$?.fileData?.$each?.[index]?.custom_contract_key?.$dirty &&
                  v$?.fileData?.$each?.[index]?.custom_contract_key?.$error
                "
                class="invalid-feedback"
              >
                {{
                  getFirstErrorMessage(
                    v$?.fileData?.$each?.[index]?.custom_contract_key?.$errors
                  )
                }}
              </div>
            </div>

            <div
              v-if="
                v$?.fileData?.$each?.[index]?.contract_key_selection?.$dirty &&
                v$?.fileData?.$each?.[index]?.contract_key_selection?.$error
              "
              class="invalid-feedback"
            >
              {{
                getFirstErrorMessage(
                  v$?.fileData?.$each?.[index]?.contract_key_selection?.$errors
                )
              }}
            </div>
          </div>

          <!-- File Title -->
          <div class="mb-3">
            <label :for="`fileTitle-${index}`" class="form-label fw-bold"
              >File title *</label
            >
            <input
              type="text"
              class="form-control"
              :id="`fileTitle-${index}`"
              v-model="file.title"
              @blur="v$?.fileData?.$each?.[index]?.title?.$touch()"
              :class="{
                'is-invalid':
                  v$?.fileData?.$each?.[index]?.title?.$dirty &&
                  v$?.fileData?.$each?.[index]?.title?.$error,
              }"
            />
            <div
              v-if="
                v$?.fileData?.$each?.[index]?.title?.$dirty &&
                v$?.fileData?.$each?.[index]?.title?.$error
              "
              class="invalid-feedback"
            >
              {{
                getFirstErrorMessage(
                  v$?.fileData?.$each?.[index]?.title?.$errors
                )
              }}
            </div>
          </div>

          <!-- File Description -->
          <div class="mb-3">
            <label :for="`fileDescription-${index}`" class="form-label"
              >File description</label
            >
            <textarea
              class="form-control"
              :id="`fileDescription-${index}`"
              v-model="file.description"
              rows="3"
            ></textarea>
          </div>

          <!-- Contract Version (only for Soldera membership keys) -->
          <div v-if="isSolderaMembershipKey(file)" class="mb-3">
            <label :for="`contractVersion-${index}`" class="form-label fw-bold"
              >Contract version</label
            >
            <select
              class="form-control"
              :id="`contractVersion-${index}`"
              v-model="file.contract_version"
            >
              <option value="v5">v5 (new)</option>
              <option value="v4">v4 (old)</option>
            </select>
          </div>

          <!-- Upload Progress for this file -->
          <div v-if="file.uploadProgress" class="mt-3">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <small>Upload Progress</small>
              <small>{{ file.uploadProgress.status }}</small>
            </div>
            <div class="progress" style="height: 4px">
              <div
                class="progress-bar"
                :class="{
                  'bg-success': file.uploadProgress.status === 'Completed',
                  'bg-danger': file.uploadProgress.status === 'Failed',
                  'bg-primary': file.uploadProgress.status === 'Uploading',
                }"
                :style="{ width: file.uploadProgress.progress + '%' }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Submit button -->
    <div class="d-flex justify-content-end mt-3">
      <button
        class="btn btn-success"
        @click="submitCustomContract"
        :disabled="isLoading || !isFormValid"
      >
        <span
          v-if="isLoading"
          class="spinner-border spinner-border-sm me-2"
        ></span>
        <font-awesome-icon v-else icon="check" class="me-2" />
        Submit custom contract
      </button>
    </div>

    <!-- Overall validation errors -->
    <div v-if="globalErrors.length > 0" class="alert alert-danger mt-3">
      <ul class="mb-0">
        <li v-for="error in globalErrors" :key="error">{{ error }}</li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAccountStore } from "@/composables/account/useAccountStore";
import { formatFileSize } from "@/composables/formatUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useValidators } from "@/composables/useValidators";
import {
  ALL_CONTRACT_KEYS,
  CONTRACT_KEYS_BY_CATEGORY,
} from "@/signup/contract_keys";
import { Account } from "@/types";
import { useVuelidate } from "@vuelidate/core";
import * as validators from "@vuelidate/validators";
import { computed, inject, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

interface FileData {
  file: File;
  contract_key_selection: string;
  custom_contract_key: string;
  title: string;
  description: string;
  contract_version: string;
  file_url: string;
  uploadProgress?: {
    progress: number;
    status: string;
  };
}

function isSolderaMembershipKey(file: FileData): boolean {
  const key =
    file.contract_key_selection === "custom"
      ? file.custom_contract_key
      : file.contract_key_selection;
  if (!key) return false;
  return (
    key === "ee_soldera_liikmeleping" ||
    key.endsWith("_soldera_membership_agreement")
  );
}

// Props
const props = defineProps<{
  accountData: Account;
  companyIndex: number;
}>();

// Composables
const { t } = useI18n();
const { required } = useValidators(t);
const accountStore = useAccountStore();

const globalErrorHandler = inject("globalErrorHandler") as (error: any) => void;
const handleSuccess = inject("globalSuccessHandler") as (
  message: string,
  title: string
) => void;

// State
const requiresSignature = ref(true);
const isLoading = ref(false);
const globalErrors = ref<string[]>([]);
const fileInput = useTemplateRef<HTMLInputElement>("fileInput");
const fileData = ref<FileData[]>([]);

// Multi-signer functionality
const selectedSigners = ref<number[]>(
  props.accountData.id ? [props.accountData.id] : []
);
const companyAccounts = ref<Account[]>([]);
const loadingAccounts = ref(false);

// Contract keys
const contractKeysByCategory = CONTRACT_KEYS_BY_CATEGORY;

// Custom validators with proper error messages
const contractKeyPrefix = validators.helpers.withMessage(
  "Custom contract key must start with 'contract_' or 'memo_'",
  (value: string) => {
    if (!value) return true; // Let required handle empty values
    return value.startsWith("contract_") || value.startsWith("memo_");
  }
);

const requiredIfCustom = validators.helpers.withMessage(
  "Custom contract key is required",
  (value: string, parent: FileData) =>
    parent.contract_key_selection !== "custom" || !!value?.trim()
);

// Computed properties
const selectedCompany = computed(
  () => props.accountData.companies[props.companyIndex]
);

const fileValidationRules = computed(() => ({
  fileData: {
    $each: validators.helpers.forEach({
      contract_key_selection: { required },
      custom_contract_key: {
        requiredIfCustom,
        contractKeyPrefix,
      },
      title: { required },
    }),
  },
}));

const signersError = computed(() => {
  if (requiresSignature.value && selectedSigners.value.length === 0) {
    return "At least one signer must be selected when signature is required";
  }
  return null;
});

const isFormValid = computed(() => {
  return !signersError.value && !v$.value.$invalid && fileData.value.length > 0;
});

// Create Vuelidate instance - without autoDirty to prevent immediate errors
const v$ = useVuelidate(
  fileValidationRules,
  { fileData },
  { $autoDirty: false }
);

// Helper function to safely get first error message
const getFirstErrorMessage = (errors: any[]): string =>
  errors?.[0]?.$message ?? "Invalid input";

// Fetch company accounts for signers
async function fetchCompanyAccounts() {
  if (!selectedCompany.value.id) return;

  try {
    loadingAccounts.value = true;
    const response = await goSolidApi.get(
      `/api/company/${selectedCompany.value.id}/accounts`
    );
    companyAccounts.value = response.data.accounts || [];
  } catch (error) {
    globalErrorHandler(error);
    companyAccounts.value = [];
  } finally {
    loadingAccounts.value = false;
  }
}

function onContractKeyChange(index: number) {
  const file = fileData.value[index];
  if (file.contract_key_selection === "custom") {
    file.custom_contract_key = "contract_";
    file.title = "";
  } else if (file.contract_key_selection) {
    // Auto-fill title based on contract key selection
    const contractKey = ALL_CONTRACT_KEYS.find(
      (key) => key.key === file.contract_key_selection
    );
    file.title = contractKey?.title || "";
    file.custom_contract_key = "";
  } else {
    file.title = "";
    file.custom_contract_key = "";
  }
}

function handleFileSelection(event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files) {
    const files = Array.from(target.files);
    fileData.value = files.map((file) => ({
      file,
      contract_key_selection: "",
      custom_contract_key: "",
      title: "",
      description: "",
      contract_version: "v5",
      file_url: "",
    }));
    // Reset validation when new files are added
    v$.value.$reset();
  }
}

function removeFile(index: number) {
  fileData.value.splice(index, 1);
  if (fileData.value.length === 0 && fileInput.value) {
    fileInput.value.value = "";
  }
  // Reset validation when files are removed
  v$.value.$reset();
}

async function uploadFile(fileDataItem: FileData): Promise<string> {
  const contractKey =
    fileDataItem.contract_key_selection === "custom"
      ? fileDataItem.custom_contract_key
      : fileDataItem.contract_key_selection;

  const formData = new FormData();
  formData.append("file", fileDataItem.file);
  formData.append("company_id", selectedCompany.value.id?.toString() || "");
  formData.append("contract_key", contractKey);

  fileDataItem.uploadProgress = {
    progress: 0,
    status: "Uploading",
  };

  try {
    const response = await goSolidApi.post(
      "/api/admin/company/custom_file_upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    fileDataItem.uploadProgress.progress = 100;
    fileDataItem.uploadProgress.status = "Completed";
    return response.data.file_url;
  } catch (error) {
    if (fileDataItem.uploadProgress) {
      fileDataItem.uploadProgress.status = "Failed";
    }
    throw new Error(`Failed to upload ${fileDataItem.file.name}`, {
      cause: error,
    });
  }
}

async function submitCustomContract() {
  try {
    isLoading.value = true;
    globalErrors.value = [];

    // Trigger validation
    await v$.value.$validate();

    // Validate form
    if (!isFormValid.value) {
      globalErrors.value.push(
        "Please fix all validation errors before submitting"
      );
      return;
    }

    // Upload all files
    const uploadPromises = fileData.value.map(async (fileDataItem) => {
      const fileUrl = await uploadFile(fileDataItem);
      fileDataItem.file_url = fileUrl;

      return {
        contract_key:
          fileDataItem.contract_key_selection === "custom"
            ? fileDataItem.custom_contract_key
            : fileDataItem.contract_key_selection,
        title: fileDataItem.title,
        description: fileDataItem.description,
        file_url: fileUrl,
        ...(isSolderaMembershipKey(fileDataItem)
          ? { contract_version: fileDataItem.contract_version }
          : {}),
      };
    });

    const results = await Promise.allSettled(uploadPromises);

    // Filter successful uploads and collect errors
    const files: any[] = [];
    const uploadErrors: string[] = [];

    results.forEach((result, index) => {
      if (result.status === "fulfilled") {
        files.push(result.value);
      } else {
        uploadErrors.push(
          `Failed to upload ${fileData.value[index].file.name}: ${result.reason}`
        );
      }
    });

    // If all uploads failed, show errors and return
    if (files.length === 0) {
      globalErrors.value = uploadErrors;
      return;
    }

    // If some uploads failed, show warnings but continue
    if (uploadErrors.length > 0) {
      globalErrors.value = uploadErrors.map((error) => `Warning: ${error}`);
    }

    await accountStore.postAccountChange("/api/admin/company/custom_contract", {
      company_id: selectedCompany.value.id,
      requires_signature: requiresSignature.value,
      files: files,
      selected_signers: selectedSigners.value,
    });

    handleSuccess(
      "New custom contract submitted",
      "Can be found in contracts section"
    );

    // Reset form
    fileData.value = [];
    if (fileInput.value) {
      fileInput.value.value = "";
    }
    v$.value.$reset();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
}

// Lifecycle
onMounted(() => {
  fetchCompanyAccounts();
});
</script>

<style scoped lang="scss">
.admin-contract-upload-component {
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}
</style>
